import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, InputNumber,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'default',
  APPROVED: 'success',
  DISPATCHED: 'cyan',
  CANCELLED: 'error',
}

const SCRAP_TYPES = ['METAL', 'COTTON_WASTE', 'PACKING', 'ELECTRICAL', 'OTHER']
const STATUSES = ['PENDING', 'APPROVED', 'DISPATCHED', 'CANCELLED']

export default function ScrapDisposalPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const grossWeight = Form.useWatch('gross_weight_kg', form)
  const tareWeight = Form.useWatch('tare_weight_kg', form)
  const netWeight = (Number(grossWeight) || 0) - (Number(tareWeight) || 0)

  const { data: scraps, isLoading } = useQuery({
    queryKey: ['scrap-disposals', plantId, statusFilter],
    queryFn: () =>
      apiClient.get(`${API}/scrap-disposals`, {
        params: { plant_id: plantId, status: statusFilter || undefined },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/scrap-disposals`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scrap-disposals'] })
      message.success('Scrap disposal created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiClient.post(`${API}/scrap-disposals/${id}/${action}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scrap-disposals'] })
      message.success('Updated')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(scraps) ? scraps : []

  const columns = [
    {
      title: 'Scrap No',
      dataIndex: 'scrap_no',
      key: 'scrap_no',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Scrap Type',
      dataIndex: 'scrap_type',
      key: 'scrap_type',
      width: 130,
      render: (v: string) => <Tag>{v?.replace('_', ' ')}</Tag>,
    },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot_no', width: 110 },
    {
      title: 'Gross (kg)',
      dataIndex: 'gross_weight_kg',
      key: 'gross_weight_kg',
      width: 100,
      align: 'right' as const,
      render: (v: number) => Number(v).toFixed(2),
    },
    {
      title: 'Tare (kg)',
      dataIndex: 'tare_weight_kg',
      key: 'tare_weight_kg',
      width: 100,
      align: 'right' as const,
      render: (v: number) => Number(v).toFixed(2),
    },
    {
      title: 'Net (kg)',
      dataIndex: 'net_weight_kg',
      key: 'net_weight_kg',
      width: 100,
      align: 'right' as const,
      render: (v: number) => <b>{Number(v).toFixed(2)}</b>,
    },
    { title: 'Buyer', dataIndex: 'buyer_name', key: 'buyer_name', width: 140 },
    { title: 'Vehicle No', dataIndex: 'vehicle_no', key: 'vehicle_no', width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, r: any) => (
        <Space>
          {r.status === 'PENDING' && (
            <Button
              size="small"
              type="primary"
              loading={actionMut.isPending}
              onClick={() => actionMut.mutate({ id: r.id, action: 'approve' })}
            >
              Approve
            </Button>
          )}
          {r.status === 'APPROVED' && (
            <Button
              size="small"
              loading={actionMut.isPending}
              onClick={() => actionMut.mutate({ id: r.id, action: 'dispatch' })}
            >
              Dispatch
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <DeleteOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Scrap Disposal
      </Title>
      <Typography.Text type="secondary">Scrap disposal authorization and tracking</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Space wrap>
              <Select
                value={plantId}
                onChange={setPlantId}
                style={{ width: 200 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
              />
              <Select
                allowClear
                placeholder="Filter by Status"
                style={{ width: 150 }}
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                options={STATUSES.map(s => ({ value: s, label: s }))}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); setDrawerOpen(true) }}
            >
              New Scrap Disposal
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Drawer
        title="New Scrap Disposal"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={480}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals => {
                    const net = (Number(vals.gross_weight_kg) || 0) - (Number(vals.tare_weight_kg) || 0)
                    createMut.mutate({ ...vals, plant_id: plantId, net_weight_kg: net })
                  })
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="lot_no" label="Lot No">
            <Input />
          </Form.Item>
          <Form.Item name="scrap_type" label="Scrap Type" rules={[{ required: true }]}>
            <Select options={SCRAP_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="gross_weight_kg" label="Gross Weight (kg)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tare_weight_kg" label="Tare Weight (kg)">
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
            <Col>
              <Typography.Text type="secondary">Net Weight: </Typography.Text>
              <Typography.Text strong>{netWeight.toFixed(2)} kg</Typography.Text>
            </Col>
          </Row>
          <Form.Item name="yard_location" label="Yard Location">
            <Input />
          </Form.Item>
          <Form.Item name="buyer_name" label="Buyer Name">
            <Input />
          </Form.Item>
          <Form.Item name="sale_ref" label="Sale Reference">
            <Input />
          </Form.Item>
          <Form.Item name="vehicle_no" label="Vehicle No">
            <Input />
          </Form.Item>
          <Form.Item name="driver_name" label="Driver Name">
            <Input />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
